import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileDown, ArrowRight, ArrowLeft } from 'lucide-react';
import { exitSteps } from '@/components/docs/exitStepsData';
import { DocStepCard } from '@/components/docs/DocStepCard';
import { useNavigate } from 'react-router-dom';

function LifecycleStage({ name, colorClass }) {
  return <span className={`px-3 py-1.5 rounded-full font-medium border ${colorClass}`}>{name}</span>;
}

function LifecycleArrow() {
  return <ArrowRight className="h-4 w-4 text-muted-foreground" />;
}

function LifecycleFlow() {
  return (
    <Card className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 border-orange-200 dark:border-orange-800">
      <CardContent className="py-5">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Employee Lifecycle</p>
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <LifecycleStage name="Active" colorClass="bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-300" />
          <LifecycleArrow />
          <LifecycleStage name="On Notice" colorClass="bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/40 dark:text-orange-300" />
          <LifecycleArrow />
          <LifecycleStage name="Settlement" colorClass="bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/40 dark:text-orange-300" />
          <LifecycleArrow />
          <LifecycleStage name="Finalize" colorClass="bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/40 dark:text-orange-300" />
          <LifecycleArrow />
          <LifecycleStage name="Exited" colorClass="bg-gray-200 text-gray-700 border-gray-400 dark:bg-gray-800 dark:text-gray-300" />
          <LifecycleArrow />
          <LifecycleStage name="Re-hire" colorClass="bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/40 dark:text-blue-300" />
        </div>
      </CardContent>
    </Card>
  );
}

function generatePDF() {
  import('jspdf').then(({ jsPDF }) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();
    let y = 20;
    const check = (n) => { if (y + n > 270) { doc.addPage(); y = 20; } };

    doc.setFontSize(22);
    doc.setTextColor(234, 88, 12);
    doc.text('Employee Exit & Settlement', 20, y);
    y += 10;
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text('Process Documentation — PayTrack', 20, y);
    y += 6;
    doc.setDrawColor(234, 88, 12);
    doc.setLineWidth(0.5);
    doc.line(20, y, pw - 20, y);
    y += 12;

    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'bold');
    doc.text('Employee Lifecycle:', 20, y);
    y += 7;
    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    doc.text('Active  >  On Notice  >  Settlement  >  Finalize  >  Exited  >  [Re-hire]', 20, y);
    y += 14;

    for (let i = 0; i < exitSteps.length; i++) {
      const step = exitSteps[i];
      check(50);
      doc.setFontSize(13);
      doc.setTextColor(234, 88, 12);
      doc.setFont(undefined, 'bold');
      doc.text((i + 1) + '. ' + step.title, 20, y);
      y += 3;
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text('Page: ' + step.page, 20, y + 3);
      y += 9;

      doc.setFontSize(10);
      doc.setTextColor(40, 40, 40);
      doc.setFont(undefined, 'normal');
      for (let j = 0; j < step.content.length; j++) {
        check(8);
        const lines = doc.splitTextToSize('- ' + step.content[j], pw - 45);
        doc.text(lines, 24, y);
        y += lines.length * 5;
      }
      y += 2;

      if (step.details) {
        for (let j = 0; j < step.details.length; j++) {
          check(12);
          doc.setFont(undefined, 'bold');
          doc.text(step.details[j].label + ':', 28, y);
          doc.setFont(undefined, 'normal');
          const dl = doc.splitTextToSize(step.details[j].desc, pw - 75);
          doc.text(dl, 65, y);
          y += Math.max(dl.length * 5, 6);
        }
      }

      if (step.note) {
        check(12);
        doc.setFontSize(9);
        doc.setTextColor(30, 80, 160);
        const nl = doc.splitTextToSize('Note: ' + step.note, pw - 50);
        doc.text(nl, 24, y);
        y += nl.length * 4.5 + 2;
      }

      if (step.important) {
        check(12);
        doc.setFontSize(9);
        doc.setTextColor(180, 80, 0);
        const il = doc.splitTextToSize('Important: ' + step.important, pw - 50);
        doc.text(il, 24, y);
        y += il.length * 4.5 + 2;
      }

      y += 8;
      doc.setTextColor(40, 40, 40);
    }

    check(30);
    doc.setFontSize(12);
    doc.setTextColor(234, 88, 12);
    doc.setFont(undefined, 'bold');
    doc.text('Settlement Formula:', 20, y);
    y += 8;
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.setFont(undefined, 'normal');
    doc.text('Bank Transfer = Total Notice Salary + Reward + Retention - Recovery - Income Tax', 20, y);

    doc.save('Employee_Exit_Settlement_Guide.pdf');
  });
}

function RoleRow({ role, perms }) {
  return (
    <div className="flex gap-2">
      <span className="font-semibold min-w-[140px]">{role}:</span>
      <span className="text-muted-foreground">{perms}</span>
    </div>
  );
}

function RoleTable() {
  return (
    <div className="space-y-2 text-sm">
      <RoleRow role="Super Admin" perms="Mark on Notice, Edit Settlement, Finalize, Unfinalize, Re-hire, Add Comments" />
      <RoleRow role="Admin" perms="Mark on Notice, Edit Settlement, Finalize, Re-hire, Add Comments (cannot Unfinalize)" />
      <RoleRow role="Payroll Auditor" perms="View Settlement, Edit amounts, Add Comments" />
      <RoleRow role="Others" perms="View only (if authorized)" />
    </div>
  );
}

function StepsList() {
  const cards = [];
  for (let i = 0; i < exitSteps.length; i++) {
    cards.push(<DocStepCard key={exitSteps[i].id} step={exitSteps[i]} index={i} />);
  }
  return <div className="space-y-3">{cards}</div>;
}

export default function ResignedEmployeeDocsPage() {
  const navigate = useNavigate();
  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6" data-testid="docs-page">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/docs')}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2 cursor-pointer"
            data-testid="back-to-docs-btn"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Docs
          </button>
          <h1 className="text-2xl font-bold tracking-tight">Employee Exit & Settlement</h1>
          <p className="text-muted-foreground mt-1">Step-by-step guide for managing employee notice periods, settlements, and exits</p>
        </div>
        <Button onClick={generatePDF} variant="outline" className="gap-2" data-testid="download-pdf-btn">
          <FileDown className="h-4 w-4" />
          Download PDF
        </Button>
      </div>

      <LifecycleFlow />

      <Card className="border-dashed">
        <CardContent className="py-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Settlement Formula</p>
          <p className="font-mono text-sm">
            <span className="text-emerald-600 font-semibold">Bank Transfer</span> = Total Notice Salary + Reward + Retention
            <span className="text-red-600"> - Recovery - Income Tax</span>
          </p>
        </CardContent>
      </Card>

      <StepsList />

      <Card>
        <CardContent className="py-5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Role Permissions</p>
          <RoleTable />
        </CardContent>
      </Card>
    </div>
  );
}
